/**
 * Carrousel de banniere.
 *
 * Le composant ne deplace jamais les diapositives lui-meme : il fait defiler le
 * conteneur et laisse le navigateur poser l'accroche. La position courante est
 * lue au scroll, pas memorisee : glisser au doigt et cliquer une puce menent
 * donc au meme etat, sans desynchronisation possible.
 */
class LioraCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    if (!this.track || this.slides.length < 1) return;

    this.index = 0;
    this.timer = null;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 6000);

    this.onScroll = this.onScroll.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('keydown', this.onKey);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.go(parseInt(dot.dataset.heroDot, 10), true));
    });

    // La lecture auto s'arrete des que le visiteur s'occupe du carrousel, et ne
    // repart pas : relancer un defilement sous le curseur ferait perdre le volet
    // qu'il etait en train de lire.
    ['pointerenter', 'focusin', 'pointerdown'].forEach((evt) => {
      this.addEventListener(evt, () => this.stop(), { once: true });
    });

    this.sync();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.track?.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  start() {
    if (this.dataset.autoplay !== 'true' || this.reduced || this.slides.length < 2) return;
    this.stop();
    this.timer = window.setInterval(() => this.go(this.index + 1, false), this.interval);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  onVisibility() {
    if (document.hidden) this.stop();
    else this.start();
  }

  onKey(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.index + 1, true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.index - 1, true);
    }
  }

  onScroll() {
    window.clearTimeout(this.settle);
    this.settle = window.setTimeout(() => {
      const width = this.track.clientWidth || 1;
      const next = Math.round(this.track.scrollLeft / width);
      if (next !== this.index) {
        this.index = next;
        this.sync();
      }
    }, 90);
  }

  go(target, byUser) {
    const total = this.slides.length;
    const next = ((target % total) + total) % total;
    if (byUser) this.stop();

    this.track.scrollTo({
      left: this.track.clientWidth * next,
      behavior: this.reduced ? 'auto' : 'smooth',
    });

    this.index = next;
    this.sync();
  }

  sync() {
    this.slides.forEach((slide, i) => {
      // `inert` plutot que `aria-hidden` : il retire aussi le bouton du parcours
      // clavier, alors qu'aria-hidden le laisserait focusable hors ecran.
      slide.inert = i !== this.index;
    });

    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('liora-carousel')) {
  customElements.define('liora-carousel', LioraCarousel);
}